// AwsEventDetails.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import moment from "moment-timezone";
import debounce from "lodash/debounce";

import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  Divider,
  CircularProgress,
  Popover,
  Autocomplete,
  TextField,
  Button,
  Snackbar,
} from "@mui/material";

import {
  Close,
  WebAsset,
  BusinessCenter,
  InfoOutlined,
  Square,
  IosShare,
  OpenInNew,
  ContentCopy,
  Send,
} from "@mui/icons-material";

import Loader from "../Loader/Loader";
import "./EventDetails.css";

import { StyledRating } from "../../ListView/ListView";

import {
  impactScore,
  impactLabel,
  getStatus,
  parseAck,
} from "../../../utility/eventUtils";

import {
  getServiceId,
  getAwsDescriptionbyTitle,
  getAdskEmpList,
} from "../../../Services";

import OriginalSummaryAccordion from "./OriginalSummaryAccordion";
import { shareReleaseData } from "../../../utility/shareRelease";

const AwsEventDetails = ({
  isOpen,
  onClose,
  event,
  loading = false,
  acknowledged: acknowledgedProp,
  ackMeta: ackMetaProp,
}) => {
  const [serviceId, setServiceId] = useState(null);
  const [description, setDescription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [copiedArn, setCopiedArn] = useState("");
  const [copySnackOpen, setCopySnackOpen] = useState(false);

  const open = Boolean(anchorEl);
  const popId = open ? "share-popover" : undefined;

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [formData, setFormData] = useState([]);
  const [isSubmit, setIsSubmit] = useState(false);
  const [error, setError] = useState("");
  const [helperText, setHelperText] = useState("Copy link");
  const [showAllResources, setShowAllResources] = useState(false);

  const MAX_VISIBLE_RESOURCES = 2;

  const fmt = (v) => (v && String(v).trim().length ? v : "N/A");

  const formatDateTimeUTC = (dateString) => {
    const date = moment.utc(dateString);
    if (!date.isValid()) return "N/A";
    return `${date.format("MMM DD, YYYY")} - ${date.format("HH:mm")} UTC`;
  };

  const impactValue = useMemo(() => impactScore(event?.relevance), [event?.relevance]);

  const shareLink = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const id = event?.release_id ? String(event.release_id) : "";
    return id ? `${origin}/aws/${id}` : origin;
  }, [event?.release_id]);

  const handleViewDetails = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openPopover = (e) => setAnchorEl(e.currentTarget);
  const closePopover = () => {
    setAnchorEl(null);
    setFormData([]);
    setError("");
    setHelperText("Copy link");
  };

  const getEmpData = async (q) => {
    try {
      const res = await getAdskEmpList(q);
      if (res?.status === 200 && res.data) {
        setUsers(res.data.users || []);
      }
    } catch (e) {
      console.error("getAdskEmpList failed:", e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const sendQuery = useCallback((v) => getEmpData(v), []);
  const debouncedSendQuery = useMemo(() => debounce(sendQuery, 800), [sendQuery]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    if (v && v.length >= 3) {
      setLoadingUsers(true);
      debouncedSendQuery(v);
    }
  };

  const handleEmployeeData = (_, value) => setFormData(value);

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setHelperText("Link Copied!");
    } catch {
      setHelperText("Press ⌘/Ctrl+C to copy");
    } finally {
      setTimeout(() => setHelperText("Copy link"), 1500);
    }
  };

  useEffect(() => {
    if (!isSubmit) return;
    if ((formData?.length || 0) > 0) {
      setError("");
      closePopover();
      const to = formData.map((u) => u.email);
      shareReleaseDataLocal(to);
    } else {
      setError("Employee Name is Required!");
    }
    setIsSubmit(false);
  }, [isSubmit]);

  const shareReleaseDataLocal = async (emp) => {
    await shareReleaseData(event, emp, shareLink);
  };

  useEffect(() => {
    if (isOpen && event) {
      setIsLoading(true);
      fetchData(event["vendor_service"], event["full_title"]);
    }
  }, [event, isOpen]);

  const fetchData = async (businessService, title) => {
    setServiceId(null);
    try {
      const response = await getServiceId(businessService);
      if (response?.data) setServiceId(response.data);
    } catch (error) {
      console.error("Error fetching Service ID:", error);
    }
    try {
      const response = await getAwsDescriptionbyTitle(title);
      if (response?.data) setDescription(response.data.description);
    } catch (error) {
      console.error("Error fetching description:", error);
    }
    setIsLoading(false);
  };

  // ----- Ack / status derivation -----
  const derivedAcks = useMemo(() => parseAck(event?.ack), [event?.ack]);
  const lastAck = derivedAcks.length ? derivedAcks[derivedAcks.length - 1] : null;

  const ackMetaDerived = useMemo(() => {
    if (!lastAck) return undefined;
    return {
      user: lastAck.user || lastAck.username || lastAck.name || "",
      comment: lastAck.comment || "",
      date: lastAck.date || lastAck.datetime || lastAck.timestamp || "",
    };
  }, [lastAck]);

  const ackMeta = ackMetaProp ?? event?.ackMeta ?? ackMetaDerived;
  const rawStatusKey = event?.status ?? event?.release_status;

  const ackFlagFromProps = acknowledgedProp ?? event?.acknowledged ?? false;
  const ackFlagFromData =
    (rawStatusKey && String(rawStatusKey).toUpperCase() === "ACKNOWLEDGED") ||
    derivedAcks.length > 0;

  const isAcknowledged = Boolean(ackFlagFromProps || ackFlagFromData);
  const statusKey = isAcknowledged ? "ACKNOWLEDGED" : rawStatusKey;
  const statusMeta = getStatus(statusKey) || { display: fmt(statusKey), color: "#444" };

  const prettyAckDate =
    ackMeta?.date && moment(ackMeta.date).isValid()
      ? moment(ackMeta.date).format("DD MMM YYYY")
      : ackMeta?.date || "";

  const splitResources = (resources) => {
    if (!resources) return [];
    return String(resources)
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean);
  };

  const prettyResource = (arn) => {
    const m = String(arn).match(/:function:([^:]+)$/);
    return m?.[1] || arn;
  };

  const copyArn = async (arn) => {
    try {
      await navigator.clipboard.writeText(arn);
      setCopiedArn(arn);
      setTimeout(() => setCopiedArn(""), 1000);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };


  const renderAffectedResources = (resources) => {
    const list = splitResources(resources);

    if (list.length === 0) return <span className="text-s">N/A</span>;

    const visible = showAllResources ? list : list.slice(0, MAX_VISIBLE_RESOURCES);

    return (
      <div>
        <ul className="affected-resource-list list-unstyled d-flex flex-column gap-2 m-0 p-0">
          {visible.map((arn, idx) => {
            const isCopied = copiedArn === arn;

            return (
              <li key={`${arn}-${idx}`} className="text-s mb-1">
                <Tooltip
                  title={isCopied ? "Copied!" : "Copy identifier"}
                  arrow
                  placement="top"
                  disableInteractive
                >
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => copyArn(arn)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") copyArn(arn);
                    }}
                    className="resource-pill d-inline-flex align-items-center gap-2 px-2 py-1 rounded-pill user-select-none"
                  >
                    <span className="resource-name font-monospace text-truncate flex-grow-1">
                      {prettyResource(arn)}
                    </span>

                    <span className="copy-affordance d-inline-flex align-items-center">
                      <ContentCopy fontSize="inherit" />
                    </span>
                  </span>
                </Tooltip>
              </li>
            );
          })}
        </ul>
        {list.length > MAX_VISIBLE_RESOURCES && (
          <Button
            variant="text"
            size="small"
            onClick={() => setShowAllResources((s) => !s)}
            sx={{ textTransform: "none", p: 0, mt: 0.5 }}
          >
            {showAllResources ? "Show less" : `Show all (${list.length})`}
          </Button>
        )}
      </div>
    );
  };

  // -----------------------------------

  if (!isOpen) return null;

  return isOpen && (isLoading ? (
    <Loader />
  ) : (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      {/* Header strip */}
      <div className="d-flex justify-content-between align-items-start">
        <span
          className="top-tag d-inline-block text-truncate"
          style={{ maxWidth: "250px" }}
          title={fmt(event?.vendor_service)}
        >
          {fmt(event?.vendor_service)}
        </span>

        <div className="d-flex align-items-center">
          <IconButton className="p-1 mx-2" onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      </div>

      <Box sx={{ width: 700, pl: 4, pr: 3, fontSize: "13px" }}>
        {/* Top meta */}
        <div style={{ marginBottom: 35 }}>
          <div className="text-secondary d-flex text-s" style={{ alignItems: "center" }}>
            <span>{fmt(event?.release_id)}</span>

            <Box component="h2" className="dot-content" sx={{ mx: "10px", my: 0 }}>
              &#183;
            </Box>

            <span className="mr-1">Status:</span>
            {isAcknowledged ? (
              <span
                style={{
                  fontWeight: 600,
                  color: statusMeta.color,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {(statusMeta?.display || "").toUpperCase()}
                <Tooltip
                  arrow
                  title={
                    <div style={{ fontSize: 12, lineHeight: 1.3 }}>
                      By <b>{ackMeta?.user || "—"}</b>
                      {prettyAckDate ? <> on <b>{prettyAckDate}</b>.</> : "."}
                      {ackMeta?.comment ? (
                        <>
                          <br />
                          <b>Comment</b>: {ackMeta.comment}
                        </>
                      ) : null}
                    </div>
                  }
                >
                  <InfoOutlined sx={{ fontSize: 16, color: statusMeta.color }} />
                </Tooltip>
              </span>
            ) : (
              <span style={{ color: statusMeta.color }}>
                {(statusMeta?.display || "").toUpperCase()}
              </span>
            )}

            <Box component="h2" className="dot-content" sx={{ mx: "10px", my: 0 }}>
              &#183;
            </Box>

            <span>
              Last updated on{" "}
              {moment(event?.last_updated, moment.ISO_8601, true).isValid()
                ? moment(event.last_updated).format("MMM DD YYYY")
                : "N/A"}
            </span>
          </div>

          {/* Title + actions */}
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="font-weight-bold mb-1">
              {fmt(event?.title || event?.release_title)}
            </h5>
          </div>

          <div className="d-flex align-items-center text-sm mb-2" style={{ gap: 16 }}>
            {/* Share (popover) */}
            <Button
              variant="text"
              size="small"
              onClick={openPopover}
              startIcon={<IosShare fontSize="small" />}
              sx={{ minWidth: 0, p: 0, textTransform: "none" }}
            >
              Share
            </Button>

            <Popover
              id={popId}
              open={open}
              anchorEl={anchorEl}
              onClose={closePopover}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <div className="share d-flex align-items-center px-3 py-2">
                <Autocomplete
                  multiple
                  id="employee"
                  freeSolo
                  className="w-350"
                  loading={loadingUsers}
                  options={users}
                  getOptionLabel={(option) => option.displayName}
                  filterOptions={(x) => x}
                  onChange={handleEmployeeData}
                  renderOption={(props, option) => (
                    <Typography title={option.email} sx={{ fontSize: "0.9rem" }} {...props}>
                      {option.displayName}
                    </Typography>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoFocus
                      margin="dense"
                      label="Enter Autodesk Username"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      required
                      variant="standard"
                      onChange={handleInputChange}
                      error={!!error && (formData?.length ?? 0) <= 0}
                      helperText={!!error && (formData?.length ?? 0) <= 0 ? error : ""}
                    />
                  )}
                />
                <Tooltip title="Send" arrow>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary active mx-2 mt-4"
                    onClick={() => setIsSubmit(true)}
                  >
                    <Send fontSize="xs" />
                  </button>
                </Tooltip>
                <Tooltip title={helperText} arrow>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-4"
                    onClick={handleCopyClick}
                  >
                    <ContentCopy fontSize="xs" />
                  </button>
                </Tooltip>
              </div>
            </Popover>

            {/* View details */}
            <Button
              variant="text"
              size="small"
              onClick={() => handleViewDetails(event?.release_url)}
              startIcon={<OpenInNew fontSize="small" />}
              sx={{ minWidth: 0, p: 0, textTransform: "none" }}
            >
              View Details
            </Button>
          </div>
        </div>

        {/* Loading state (optional) */}
        {loading && (
          <div className="d-flex align-items-center mb-4">
            <CircularProgress size={20} className="mr-2" />
            <span>Loading details…</span>
          </div>
        )}

        {/* Details grid */}
        <div className="row">
          {/* Business Service */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm">
              <strong>Business Service</strong>
            </p>
            <div className="d-flex align-items-start">
              <WebAsset fontSize="small" className="mr-1" />
              {serviceId ? (
                <a
                  className="text-s"
                  target="_blank"
                  href={`https://one.autodesk.com/apps/service-registry/service-components?serviceId=${serviceId}`}
                  rel="noreferrer"
                >
                  {event.vendor_service}
                  {serviceId ? ` (${serviceId})` : ""}
                </a>
              ) : (
                <span className="text-s">{event.vendor_service}</span>
              )}
              {event?.businessOwner && (
                <Tooltip className="ml-1" title={`Business Owner: ${event.businessOwner}`}>
                  <InfoOutlined sx={{ fontSize: 15, marginTop: "1px" }} />
                </Tooltip>
              )}
            </div>
          </div>

          {/* Source */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm">
              <strong>Source</strong>
            </p>
            <div className="d-flex align-items-center">
              <BusinessCenter fontSize="small" className="mr-1" />
              <span className="text-s">AWS Health</span>
            </div>
          </div>

          {/* Category */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm d-flex align-items-center">
              <strong>Category</strong>
            </p>
            <span className="text-s">
              {fmt(event?.category)?.charAt(0).toUpperCase() + fmt(event?.category)?.slice(1)}
            </span>
          </div>

          {/* Geo */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm">
              <strong>Geo</strong>
            </p>
            <span className="text-s">{fmt(event?.region)}</span>
          </div>

          {/* Starts / Ends */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm">
              <strong>Start Date</strong>
            </p>
            <span className="text-s">{formatDateTimeUTC(event?.planned_start_date)}</span>
          </div>
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm">
              <strong>End Date</strong>
            </p>
            <span className="text-s">{formatDateTimeUTC(event?.planned_end_date)}</span>
          </div>

          {/* Impact */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm d-flex align-items-center">
              <strong>Impact</strong>
            </p>
            {impactValue ? (
              <div className="d-flex align-items-center">
                <StyledRating
                  name="impact-rating"
                  value={impactValue}
                  max={3}
                  readOnly
                  icon={<Square sx={{ fontSize: "16px" }} />}
                  emptyIcon={<Square sx={{ fontSize: "16px" }} />}
                />
                <span className="ml-1 text-s">{impactLabel(event?.relevance) || "N/A"}</span>
              </div>
            ) : (
              <span className="text-s">N/A</span>
            )}
          </div>

          {/* Environment */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm">
              <strong>Environment</strong>
            </p>
            <span className="text-s">{fmt(event?.environment)}</span>
          </div>

          {/* Act By */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm">
              <strong>Act By</strong>
            </p>
            <span className="text-s">
              {formatDateTimeUTC(event?.act_by)}
            </span>
          </div>

          {/* What Action Needed */}
          <div className="col-6 mb-4">
            <p className="mb-1 text-sm">
              <strong>What Action Needed</strong>
            </p>
            <span className="text-s">{fmt(event?.what_action_needed)}</span>
          </div>

          {/* Affected Resources / Impacted Vendor Service */}
          <div className="col-12 mb-4">
            <p className="mb-1 text-sm">
              <strong>Affected Resource(s)</strong>
            </p>
            <div className="d-flex align-items-start">
              <WebAsset fontSize="small" className="mr-1" />
              {renderAffectedResources(event?.impacted_services)}
            </div>
          </div>
        </div>

        <Divider />

        {/* Summary (simple, only if present) */}
        {event?.summary && (
          <div className="mt-3">
            <div className="mb-4">
              <p className="text-body-1 mb-1">
                <strong>
                  Summary <small>(Generated by Autodesk GPT)</small>
                </strong>
              </p>
              <div
                style={{ whiteSpace: "pre-wrap" }}
                dangerouslySetInnerHTML={{ __html: event.summary }}
              />
            </div>
          </div>
        )}

        <div className="mb-4">
          <OriginalSummaryAccordion description={description} />
        </div>
      </Box>
    </Drawer>
  ));
};

export default AwsEventDetails;
